import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { IAdvertiser, IEUAccountFile, IEUAccountSendForm, IEUAccountSingle, IEUAccountServices, IEUAccountNettings, IEUAccountStatsTaskItems } from "@/api/types/revenue";
import { getCurrencyRate } from "@/api/catalogue";
import { getEUAccountFiles } from "@/api/revenue";
import { getCurrentDate, getFormatDate } from "@/utils/formatDates";
import { getDefaultTrafficPeriodForMonth, serviceToTrafficPeriodString, trafficPeriodStringToRange } from "@/views/revenue/EUAccounts/euAccountTrafficPeriod";
import { isEuAccountPercentGoal } from "@/views/revenue/EUAccounts/euAccountGoalRevenue";
import { EU_ACCOUNT_PAYABLE_TOLERANCE_PERCENT, getEuAccountPayableDifferencePercent, isEuAccountFullyNetted } from "@/views/revenue/EUAccounts/euAccountPayableTolerance";

export const useEUAccountsStore = defineStore('useEUAccountsStore', () => {
    const isLoading = ref<{ [key: string]: boolean }>({
        main: false,
        services: false,
        offsetting: false,
        files: false,
    });
    const form = ref<IEUAccountSendForm>({
        advertiser_id: null,
        currency: '',
        period: '',
        legal_entity_id: null,
        contractor_id: null,
        project: '',
        days_to_pay: null,
        auto_email: false,
        is_draft: false,
        send_to_1c: true,
        comment: '',
        netting_act_amount: null,
        services: [],
        nettings: [],
        stats: [{
            id: 0,
            services: [],
            file: null,
            tempFile: [], // Временной хранение файла
            comment: '',
        }],
    });
    const selectedAdvertiser = ref<IAdvertiser | null>(null);
    const currentRadioValue = ref('1');
    const isEdit = ref(false);
    const EUAccountObj = ref<IEUAccountSingle | null>(null);
    const isMainFormFilled = ref(false);
    const updateEUAccountsListFlag = ref(false);
    const servicesToPay = ref<IEUAccountServices[]>([]);
    /** Файлы счёта. Загружаются отдельным запросом, счёт их в себе не содержит */
    const invoiceFiles = ref<IEUAccountFile[]>([]);

    const isMainFormComplete = computed<boolean>(() => {
        return !!(
            form.value.advertiser_id !== null &&
            form.value.legal_entity_id !== null &&
            form.value.days_to_pay !== null &&
            form.value.currency &&
            form.value.contractor_id !== null &&
            form.value.period &&
            form.value.project
        );
    });

    const getServicesTotalSum = computed(() => {
        if (!form.value.services?.length && EUAccountObj.value?.services?.length) {
            return EUAccountObj.value.services?.reduce((sum, item) => sum + (item.final_amount as number), 0);
        }

        return form.value.services?.reduce((sum, item) => sum + (item.final_amount as number), 0);
    });

    const getNettingsTotalSum = computed(() => {
        if (currentRadioValue.value === '2') {
            return form.value.netting_act_amount;
        }

        if (!form.value.nettings?.length && EUAccountObj.value?.nettings?.length) {
            return EUAccountObj.value.nettings?.reduce((sum, item) => sum + (item.final_amount as number), 0);
        }

        return form.value.nettings?.reduce((sum, item) => sum + (item.final_amount as number), 0);
    });

    const getServicesToPaySelectedSum = computed(() =>
        servicesToPay.value.reduce((sum, item) => sum + Number(item.final_amount ?? 0), 0),
    );

    const getServicesToPayTargetSum = computed(() => {
        const servicesSum = Number(getServicesTotalSum.value ?? 0);
        const nettingsSum = Number(getNettingsTotalSum.value ?? 0);
        return servicesSum - nettingsSum;
    });

    const getServicesToPayDifferencePercent = computed(() =>
        getEuAccountPayableDifferencePercent(
            Number(getServicesToPaySelectedSum.value ?? 0),
            Number(getServicesToPayTargetSum.value ?? 0),
        ),
    );

    /** Счёт закрыт взаимозачётом целиком — услуги к оплате в нём не нужны. */
    const isEUAccountFullyNetted = computed(() =>
        isEuAccountFullyNetted({
            hasPayableServices: servicesToPay.value.length > 0,
            servicesSum: Number(getServicesTotalSum.value ?? 0),
            nettingsSum: Number(getNettingsTotalSum.value ?? 0),
        }),
    );

    const isServicesToPayDifferenceMoreThanOnePercent = computed(() =>
        !isEUAccountFullyNetted.value &&
            getServicesToPayDifferencePercent.value > EU_ACCOUNT_PAYABLE_TOLERANCE_PERCENT,
    );

    const clearAll = (): void => {
        form.value.advertiser_id = null,
        form.value.currency = '',
        form.value.period = '',
        form.value.legal_entity_id = null,
        form.value.contractor_id = null,
        form.value.project = '',
        form.value.days_to_pay = null,
        form.value.auto_email = false,
        form.value.is_draft = false,
        form.value.send_to_1c = true,
        form.value.comment = '',
        form.value.netting_act_amount = null,
        form.value.services = [];
        form.value.nettings = [];
        servicesToPay.value = [];
        form.value.stats = [{
            id: 0,
            services: [],
            file: null,
            comment: '',
        }];

        selectedAdvertiser.value = null;
        currentRadioValue.value = '1';
        EUAccountObj.value = null;
        invoiceFiles.value = [];
    };

    /**
     * Загрузка файлов счёта.
     * Ошибку не пробрасываем и тост не показываем: файлы — вспомогательный блок,
     * из-за них не должна падать вся страница счёта. Файлов нет — блок скрыт.
     */
    const getInvoiceFiles = async (id: number | string): Promise<void> => {
        if (!id) {
            invoiceFiles.value = [];
            return;
        }

        try {
            isLoading.value.files = true;
            invoiceFiles.value = await getEUAccountFiles(id);
        } catch (err) {
            invoiceFiles.value = [];
            console.error('Файлы евросчёта:', err);
        } finally {
            isLoading.value.files = false;
        }
    };

    const setDefaultData = (): void => {
        if (EUAccountObj.value?.id) {
            form.value.advertiser_id = EUAccountObj.value.advertiser?.id;
            form.value.currency = EUAccountObj.value.currency;
            form.value.period = EUAccountObj.value.period;
            form.value.legal_entity_id = EUAccountObj.value.legal.id;
            form.value.contractor_id = EUAccountObj.value.contractor.id;
            form.value.project = EUAccountObj.value.project;
            form.value.days_to_pay = EUAccountObj.value.days_to_pay;
            form.value.auto_email = !!EUAccountObj.value.auto_email;
            form.value.is_draft = EUAccountObj.value.is_draft;
            form.value.comment = EUAccountObj.value.comment;
            form.value.netting_act_amount = EUAccountObj.value.netting_act_amount;

            if (EUAccountObj.value.stats !== null && EUAccountObj.value.stats !== undefined) {
                const usedServiceIndices = new Set<number>();
                form.value.stats = EUAccountObj.value.stats.items.map((el: IEUAccountStatsTaskItems) => ({
                    ...el,
                    file: el.file?.id ?? null,
                    comment: '',
                    collapse: true,
                    tempFile: (el.file?.name ? [{ name: el.file.original_name, type: el.file.extension }] : []) as File[] | [],
                    services: el.services.map((e) => {
                        const allServices: IEUAccountServices[] = EUAccountObj.value?.services ?? [];
                        const statFinalRate = Number(e.final_rate ?? e.original_amount ?? 0);
                        const statQuantity = Number(e.quantity ?? 0);
                        let originalIndex = -1;
                        const pickIndex = (isStrict: boolean): number => {
                            for (let i = 0; i < allServices.length; i++) {
                                if (usedServiceIndices.has(i)) continue;
                                const s = allServices[i];
                                const isSameService =
                                    s.offer_id === e.offer_id &&
                                    (s.goal_id === e.goal_id || s.goal_service_id === e.goal_id);
                                if (!isSameService) continue;
                                if (isStrict) {
                                    const serviceOriginalAmount = Number(s.original_amount ?? 0);
                                    const serviceQuantity = Number(s.quantity ?? 0);
                                    const isSameRate = Math.abs(serviceOriginalAmount - statFinalRate) < 0.0001;
                                    const isSameQuantity = serviceQuantity === statQuantity;
                                    if (!isSameRate || !isSameQuantity) continue;
                                }
                                return i;
                            }
                            return -1;
                        };

                        originalIndex = pickIndex(true);
                        if (originalIndex < 0) {
                            originalIndex = pickIndex(false);
                        }
                        if (originalIndex >= 0) {
                            usedServiceIndices.add(originalIndex);
                        }
                        const originalService = originalIndex >= 0 ? allServices[originalIndex] : undefined;
                        return {
                            id: e.id ?? 0,
                            offer_id: e.offer_id,
                            goal_id: e.goal_id ?? e.goal_service_id ?? 0,
                            goal_service_id: e.goal_service_id ?? e.goal_id,
                            start_rate: e.start_rate ?? e.current_rate ?? e.original_amount ?? 0,
                            final_rate: e.final_rate ?? e.original_amount ?? 0,
                            quantity: e.quantity ?? 0,
                            originalIndex: originalIndex >= 0 ? originalIndex : undefined,
                            offer_name: e.offer_name,
                            goal_name: e.goal_name,
                            currency: originalService?.currency,
                            status: e.status,
                        };
                    }),
                })) as IEUAccountSendForm['stats'];
            } else {
                form.value.stats = [{
                    id: 0,
                    services: [],
                    file: null,
                    comment: '',
                }];
            }

            form.value.services = EUAccountObj.value.services?.map((el: IEUAccountServices) => ({
                ...el,
                ...{
                    goal_currency: el.currency,
                    goal_service_id: el.goal_id,
                    tempId: el.tempId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                },
            })) ?? [];

            const payableSource = EUAccountObj.value.payable_services?.length
                ? EUAccountObj.value.payable_services
                : [];
            const invoiceTraffic = {
                from: EUAccountObj.value.traffic_period_from ?? null,
                to: EUAccountObj.value.traffic_period_to ?? null,
            };
            servicesToPay.value = payableSource.map((el: IEUAccountServices) => {
                let from = el.traffic_period_from ?? invoiceTraffic.from ?? null;
                let to = el.traffic_period_to ?? invoiceTraffic.to ?? null;
                if (!from || !to) {
                    const combined =
                        serviceToTrafficPeriodString(el, invoiceTraffic) ??
                        getDefaultTrafficPeriodForMonth(form.value.period);
                    const parsed = trafficPeriodStringToRange(combined);
                    from = from ?? parsed.from;
                    to = to ?? parsed.to;
                }
                // В payable_services счёта название услуги приходит как `name`
                const serviceName = el.service_name ?? el.name;
                return {
                    ...el,
                    goal_currency: el.currency ?? el.goal_currency,
                    goal_service_id: el.goal_id ?? el.goal_service_id,
                    tempId: el.tempId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    traffic_period_from: from,
                    traffic_period_to: to,
                    service_name: serviceName,
                    // Исходное название — то же, что пришло: иначе сохранение сочтёт
                    // его изменённым и зря перезапишет название цели в offers-service
                    original_service_name: serviceName,
                    // Ключ нужен сразу: в Vue 2 новое свойство объекта не реактивно
                    unit: el.unit ?? null,
                };
            });

            if (EUAccountObj.value.nettings?.length) {
                form.value.nettings = EUAccountObj.value.nettings.map((el: IEUAccountNettings) => ({
                    ...el,
                    ...{
                        balance: el.invoice_amount! - el.payments_total!,
                        amount: el.invoice_amount,
                        partials_amount: el.payments_total,
                    },
                }));
            }
        }
    };

    const formatNumber = (num: number | string, digits: number[] = [2, 2]): string => {
        return Number(num).toLocaleString('ru-RU', {
            maximumFractionDigits: digits[0],
            minimumFractionDigits: digits[1],
        });
    };

    const recountServicesSum = (item: IEUAccountServices): void => {
        const isPercentGoal = isEuAccountPercentGoal(item);
        if (isPercentGoal) {
            item.final_amount = (item.original_amount as number) * (item.quantity as number);
        } else {
            const itemCurrency = item.goal_currency || item.currency;
            if (itemCurrency && itemCurrency !== form.value.currency) {
                if (item.operation === 'division' && item.exchange_rate) {
                    item.final_amount = (item.original_amount as number) / (item.exchange_rate as number) * (item.quantity as number);
                } else if (item.operation === 'multiplication' && item.exchange_rate) {
                    item.final_amount = (item.original_amount as number) * (item.exchange_rate as number) * (item.quantity as number);
                } else {
                    item.final_amount = (item.original_amount as number) * (item.quantity as number);
                }
            } else {
                item.final_amount = (item.original_amount as number) * (item.quantity as number);
            }
        }

        if (!isFinite(item.final_amount as number)) {
            item.final_amount = 0;
        }
    };

    const recountServiceRateFromTotal = (item: IEUAccountServices): void => {
        const quantity = Number(item.quantity) || 1;
        const total = Number(item.final_amount) || 0;
        const rate = Number(item.exchange_rate) || 0;
        const itemCurrency = item.goal_currency || item.currency;
        // ponytail: без курса считаем 1:1 — строка всё равно не даст сохранить счёт
        const isRateApplied = !isEuAccountPercentGoal(item)
            && !!itemCurrency && itemCurrency !== form.value.currency && !!rate;

        let amount = total / quantity;
        if (isRateApplied && item.operation === 'division') {
            amount = amount * rate;
        } else if (isRateApplied && item.operation === 'multiplication') {
            amount = amount / rate;
        }

        item.original_amount = isFinite(amount) ? amount : 0;
    };

    const recountNettingsSum = (item: IEUAccountNettings): void => {
        item.balance = item.amount! - item.partials_amount!;

        if (form.value.currency !== item.currency) {
            if (item.operation === 'division') {
                item.original_amount = item.balance / (item.exchange_rate as number);
            } else if (item.operation === 'multiplication') {
                item.original_amount = item.balance * (item.exchange_rate as number);
            }
        } else {
            item.original_amount = item.balance;
            item.final_amount = item.original_amount;
        }

        if (item.original_amount < item.final_amount) {
            item.final_amount = item.original_amount;
        }

        if (!isFinite(item.final_amount as number)) {
            item.final_amount = 0;
        }
        if (!isFinite(item.original_amount as number)) {
            item.original_amount = 0;
        }
    };

    const fetchRate = async ({ from, to, date }: { from: string, to: string, date: string }): Promise<{ [key: string]: unknown }> => {
        return await getCurrencyRate({
            source: 'excel',
            currency_from: from,
            currency_to: to,
            date,
        })
            .then(resp => resp)
            .catch(() => ({ operation: '', exchange_rate: 0, original_rate: 0 }));
    };

    const recountSumIfChangeCurrency = async (): Promise<void> => {
        await Promise.all(form.value.services.map(async (el: IEUAccountServices) => {
            const isPercentGoal = isEuAccountPercentGoal(el);
            if (!isPercentGoal && form.value.currency !== el.goal_currency) {
                const { operation, original_rate } = await fetchRate({
                    from: el.goal_currency!,
                    to: form.value.currency!,
                    date: getCurrentDate('yyyy-MM-dd'),
                });

                el.exchange_rate = original_rate as number;
                el.operation = operation as string;
            }

            recountServicesSum(el);
        }));

        if (form.value.nettings?.length) {
            await Promise.all(form.value.nettings.map(async (el: IEUAccountNettings) => {
                if (form.value.currency !== el.currency) {
                    const { operation, original_rate } = await fetchRate({
                        from: form.value.currency!,
                        to: el.currency!,
                        date: getFormatDate(el.created_at!, 'yyyy-MM-dd'),
                    });

                    el.exchange_rate = original_rate as number;
                    el.operation = operation as string;
                }

                recountNettingsSum(el);
            }));
        }

        await Promise.all(servicesToPay.value.map(async (el: IEUAccountServices) => {
            const isPercentGoal = isEuAccountPercentGoal(el);
            if (!isPercentGoal && form.value.currency !== el.goal_currency) {
                const { operation, original_rate } = await fetchRate({
                    from: el.goal_currency!,
                    to: form.value.currency!,
                    date: getCurrentDate('yyyy-MM-dd'),
                });

                el.exchange_rate = original_rate as number;
                el.operation = operation as string;
            }

            recountServicesSum(el);
        }));
    };

    watch(isMainFormComplete, (val: boolean) => {
        if (val) {
            isMainFormFilled.value = true;
        }
    }, { immediate: true });

    watch(() => form.value.currency, async (currency) => {
        if (!currency || (!form.value.services?.length && !form.value.nettings?.length && !servicesToPay.value.length)) return;
        if (EUAccountObj.value && !isEdit.value) return;
        await recountSumIfChangeCurrency();
    });

    return {
        isLoading,
        form,
        selectedAdvertiser,
        currentRadioValue,
        isEdit,
        EUAccountObj,
        updateEUAccountsListFlag,
        isMainFormFilled,
        servicesToPay,
        invoiceFiles,

        isMainFormComplete,
        getServicesTotalSum,
        getNettingsTotalSum,
        getServicesToPaySelectedSum,
        getServicesToPayTargetSum,
        getServicesToPayDifferencePercent,
        isEUAccountFullyNetted,
        isServicesToPayDifferenceMoreThanOnePercent,

        clearAll,
        getInvoiceFiles,
        setDefaultData,
        formatNumber,
        recountServicesSum,
        recountServiceRateFromTotal,
        recountNettingsSum,
        fetchRate,
        recountSumIfChangeCurrency,
    };
});
